import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  List,
  Avatar,
  Input,
  Button,
  Select,
  Space,
  Tag,
  message,
  Spin,
  Empty,
  Tooltip,
  Popconfirm,
  Modal,
  Form,
} from "antd";
import {
  CommentOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  getTeamComments,
  createTeamComment,
  updateTeamComment,
  deleteTeamComment,
  getTeamCommentStats,
} from "../../../api/accounting";
import useGlobalFilters from "../../../../../hooks/useGlobalFilters";
import "./TeamComments.css";

const { TextArea } = Input;
const { Option } = Select;

const TeamComments = ({ selectedBrand, showStats = true, maxHeight = 400 }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("info");
  const [editingComment, setEditingComment] = useState(null);
  const [editForm] = Form.useForm();
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const { brandIds } = useGlobalFilters();

  // Get the brand filter for API calls
  const getBrandFilter = useCallback(() => {
    if (selectedBrand) {
      return selectedBrand.id;
    }
    if (brandIds && brandIds.length > 0) {
      return brandIds.join(",");
    }
    return null;
  }, [selectedBrand, brandIds]);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    const brandFilter = getBrandFilter();
    if (!brandFilter) return;

    setLoading(true);
    try {
      const response = await getTeamComments({
        brand: brandFilter,
        ordering: "-created_at",
      });
      setComments(response.data.results || []);
    } catch (error) {
      console.error("Error fetching team comments:", error);
      message.error("Failed to load team comments");
    } finally {
      setLoading(false);
    }
  }, [getBrandFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!showStats) return;

    const brandFilter = getBrandFilter();
    if (!brandFilter) return;

    try {
      const response = await getTeamCommentStats({
        brand: brandFilter,
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching team comment stats:", error);
    }
  }, [getBrandFilter, showStats]);

  // Initial load and brand change
  useEffect(() => {
    fetchComments();
    fetchStats();
  }, [fetchComments, fetchStats]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchComments(), fetchStats()]);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle submit new comment
  const handleSubmit = async () => {
    if (!newComment.trim()) {
      message.warning("Please enter a comment");
      return;
    }

    const brandFilter = getBrandFilter();
    if (!brandFilter) {
      message.error("Please select a brand");
      return;
    }

    setSubmitting(true);
    try {
      // Get a single brand ID for comment creation
      let brandId = null;
      if (selectedBrand?.id) {
        brandId = selectedBrand.id;
      } else if (brandIds && brandIds.length > 0) {
        brandId = brandIds[0]; // Use first brand if multiple selected
      }

      const commentData = {
        message: newComment.trim(),
        comment_type: commentType,
        brand: brandId,
      };

      await createTeamComment(commentData);
      setNewComment("");
      setCommentType("info");
      message.success("Comment added successfully");
      await fetchComments();
      await fetchStats();
    } catch (error) {
      console.error("Error creating comment:", error);
      message.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit comment
  const handleEdit = (comment) => {
    setEditingComment(comment);
    editForm.setFieldsValue({
      message: comment.message,
      comment_type: comment.comment_type,
    });
  };

  // Handle update comment
  const handleUpdate = async (values) => {
    if (!editingComment) return;

    try {
      await updateTeamComment(editingComment.id, values);
      setEditingComment(null);
      editForm.resetFields();
      message.success("Comment updated successfully");
      await fetchComments();
      await fetchStats();
    } catch (error) {
      console.error("Error updating comment:", error);
      message.error("Failed to update comment");
    }
  };

  // Handle delete comment
  const handleDelete = async (commentId) => {
    try {
      await deleteTeamComment(commentId);
      message.success("Comment deleted successfully");
      await fetchComments();
      await fetchStats();
    } catch (error) {
      console.error("Error deleting comment:", error);
      message.error("Failed to delete comment");
    }
  };

  // Get comment type icon and color
  const getCommentTypeConfig = (type) => {
    const configs = {
      info: { icon: <InfoCircleOutlined />, color: "blue" },
      success: { icon: <CheckCircleOutlined />, color: "green" },
      warning: { icon: <ExclamationCircleOutlined />, color: "orange" },
      error: { icon: <CloseCircleOutlined />, color: "red" },
    };
    return configs[type] || configs.info;
  };

  // Get user initials for avatar
  const getUserInitials = (comment) => {
    return comment.user_initials || "U";
  };

  const brandFilter = getBrandFilter();

  return (
    <div className="team-comments">
      {/* Stats Section */}
      {showStats && stats && (
        <Card
          size="small"
          className="team-comments-stats"
          style={{ marginBottom: 16 }}
        >
          <Space size="large" wrap>
            <div>
              <strong>Total Comments:</strong> {stats.total_comments}
            </div>
            <div>
              <strong>Today:</strong> {stats.today_comments}
            </div>
            <div>
              <strong>This Week:</strong> {stats.week_comments}
            </div>
            <Space>
              <Tag color="blue">Info: {stats.by_type?.info || 0}</Tag>
              <Tag color="green">Success: {stats.by_type?.success || 0}</Tag>
              <Tag color="orange">Warning: {stats.by_type?.warning || 0}</Tag>
              <Tag color="red">Error: {stats.by_type?.error || 0}</Tag>
            </Space>
          </Space>
        </Card>
      )}

      {/* Main Comments Card */}
      <Card
        title={
          <Space>
            <CommentOutlined />
            Team Comments
            {comments.length > 0 && <Tag color="blue">{comments.length}</Tag>}
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing}
            size="small"
          >
            Refresh
          </Button>
        }
        className="team-comments-card"
      >
        {/* New Comment Form */}
        {brandFilter && (
          <div className="new-comment-form" style={{ marginBottom: 16 }}>
            <Space.Compact style={{ width: "100%" }}>
              <Select
                value={commentType}
                onChange={setCommentType}
                style={{ width: 100 }}
                size="small"
              >
                <Option value="info">
                  <Space>
                    <InfoCircleOutlined style={{ color: "#1890ff" }} />
                    Info
                  </Space>
                </Option>
                <Option value="success">
                  <Space>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    Success
                  </Space>
                </Option>
                <Option value="warning">
                  <Space>
                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                    Warning
                  </Space>
                </Option>
                <Option value="error">
                  <Space>
                    <CloseCircleOutlined style={{ color: "#f5222d" }} />
                    Error
                  </Space>
                </Option>
              </Select>
              <TextArea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a team comment..."
                autoSize={{ minRows: 2, maxRows: 4 }}
                onPressEnter={(e) => {
                  if (e.shiftKey) return; // Allow shift+enter for new line
                  e.preventDefault();
                  handleSubmit();
                }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSubmit}
                loading={submitting}
                disabled={!newComment.trim()}
              >
                Post
              </Button>
            </Space.Compact>
          </div>
        )}

        {/* No Brand Selected */}
        {!brandFilter && (
          <Empty
            description="Please select a brand to view and add team comments"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {/* Comments List */}
        {brandFilter && (
          <div
            className="comments-list"
            style={{
              maxHeight: maxHeight,
              overflowY: "auto",
            }}
          >
            <Spin spinning={loading}>
              {comments.length === 0 && !loading ? (
                <Empty
                  description="No team comments yet"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={comments}
                  renderItem={(comment) => {
                    const typeConfig = getCommentTypeConfig(
                      comment.comment_type
                    );
                    return (
                      <List.Item
                        actions={[
                          <Tooltip key="edit" title="Edit">
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEdit(comment)}
                            />
                          </Tooltip>,
                          <Popconfirm
                            key="delete"
                            title="Delete this comment?"
                            onConfirm={() => handleDelete(comment.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Tooltip title="Delete">
                              <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                              />
                            </Tooltip>
                          </Popconfirm>,
                        ]}
                        className="comment-item"
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              size="small"
                              style={{
                                backgroundColor: "#1890ff",
                                fontSize: "12px",
                              }}
                              icon={<UserOutlined />}
                            >
                              {getUserInitials(comment)}
                            </Avatar>
                          }
                          title={
                            <Space>
                              <span className="comment-user">
                                {comment.user_display_name}
                              </span>
                              <Tag
                                color={typeConfig.color}
                                icon={typeConfig.icon}
                                size="small"
                              >
                                {comment.comment_type}
                              </Tag>
                              <span className="comment-time">
                                {comment.time_ago}
                              </span>
                              {comment.brand_name && (
                                <Tag size="small">{comment.brand_name}</Tag>
                              )}
                            </Space>
                          }
                          description={
                            <div className="comment-message">
                              {comment.message}
                            </div>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              )}
            </Spin>
          </div>
        )}
      </Card>

      {/* Edit Comment Modal */}
      <Modal
        title="Edit Comment"
        open={!!editingComment}
        onCancel={() => {
          setEditingComment(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} onFinish={handleUpdate} layout="vertical">
          <Form.Item
            name="comment_type"
            label="Type"
            rules={[{ required: true, message: "Please select a type" }]}
          >
            <Select>
              <Option value="info">
                <Space>
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                  Info
                </Space>
              </Option>
              <Option value="success">
                <Space>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  Success
                </Space>
              </Option>
              <Option value="warning">
                <Space>
                  <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                  Warning
                </Space>
              </Option>
              <Option value="error">
                <Space>
                  <CloseCircleOutlined style={{ color: "#f5222d" }} />
                  Error
                </Space>
              </Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: "Please enter a message" }]}
          >
            <TextArea rows={4} placeholder="Enter your comment..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Comment
              </Button>
              <Button
                onClick={() => {
                  setEditingComment(null);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamComments;
